import { CnDatahubCategoryColumn } from './datahub-interfaces'

export const DEFAULT_ORDER_KEY_GAP = 1000

export interface OrderKeySortable {
  orderKey: number
  datakey: string
}

/**
 * Get the default order key for an index
 * @param index - The index
 * @returns The default order key for the index
 */
export function defaultOrderKeyForIndex(index: number): number {
  return (index + 1) * DEFAULT_ORDER_KEY_GAP
}

/**
 * Get the order key between two columns
 * @param before - The order key of the column before the new column
 * @param after - The order key of the column after the new column
 * @returns The order key for the new column
 */
export function orderKeyBetween(
  before: number | null | undefined,
  after: number | null | undefined,
): number {
  // If there are no columns before or after, use the default order key
  if (before == null && after == null) {
    return defaultOrderKeyForIndex(0)
  }
  // If there is no column before, use the order key of the column after divided by 2
  if (before == null) {
    const afterKey = after as number
    if (afterKey <= 1) return afterKey - DEFAULT_ORDER_KEY_GAP
    return Math.floor(afterKey / 2)
  }
  // If there is no column after, use the order key of the column before plus the default order key gap
  if (after == null) {
    return before + DEFAULT_ORDER_KEY_GAP
  }
  // If the difference between the order keys of the columns before and after is greater than 1, use the average of the order keys of the columns before and after (ex: 1000 and 2000 -> 1500)
  if (after - before > 1) {
    return Math.floor((before + after) / 2)
  }
  // Otherwise, use the order key of the column before plus 1
  return before + 1
}

/**
 * Sort the items by order key
 * @param items - The items to sort
 * @returns The sorted items
 */
export function sortByOrderKey<T extends OrderKeySortable>(items: T[]): T[] {
  // Sort the items by order key
  return [...items].sort((a, b) => {
    if (a.orderKey !== b.orderKey) return a.orderKey - b.orderKey
    return a.datakey.localeCompare(b.datakey)
  })
}

/**
 * Get the order key for a column to be moved
 * @param sortedItems - The sorted items
 * @param fromIndex - The index of the column to move
 * @param toIndex - The new index of the column
 * @returns The order key for the column to be moved
 */
export function orderKeyForMove<T extends OrderKeySortable>(
  sortedItems: T[],
  fromIndex: number,
  toIndex: number,
): number {
  // Get the items without the moved item
  const withoutMoved = sortedItems.filter((_, index) => index !== fromIndex)
  // Get the order key of the column before the new column and the column after the new column
  const before = toIndex > 0 ? withoutMoved[toIndex - 1]?.orderKey ?? null : null
  const after = toIndex < withoutMoved.length ? withoutMoved[toIndex]?.orderKey ?? null : null
  // Get the order key between the column before the new column and the column after the new column
  return orderKeyBetween(before, after)
}

/**
 * Get the order key after the last column
 * @param items - The items
 * @returns The order key after the last column
 */
export function orderKeyAfterLast<T extends OrderKeySortable>(items: T[]): number {
  // If there are no items, use the default order key
  if (items.length === 0) return defaultOrderKeyForIndex(0)
  // Sort the items by order key
  const sorted = sortByOrderKey(items)
  // Get the order key after the last column (ex: if last column is 1000, return 1100)
  return orderKeyBetween(sorted[sorted.length - 1].orderKey, null)
}

/**
 * Reassign sequential order keys (private categories — no fractional "between" keys).
 * @param items - Items already in the desired display order
 */
export function reindexOrderKeys<T extends OrderKeySortable>(items: T[]): T[] {
  return items.map((item, index) => ({
    ...item,
    orderKey: defaultOrderKeyForIndex(index),
  }))
}

/**
 * Sort the datahub columns by order key
 * @param columns - The columns to sort
 * @returns The sorted columns
 */
export function sortDatahubColumns(columns: CnDatahubCategoryColumn[]): CnDatahubCategoryColumn[] {
  return sortByOrderKey(columns)
}
